import { useEffect, useRef, useState } from "react";
import { Pencil, Plus, Trash2, Loader2, X, ChevronUp, ChevronDown, Copy, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { name: "", category: "", price_label: "", duration: 30, description: "", active: true, admin_only: false, order: 0 };

export const ServicesAdmin = () => {
  const [services, setServices] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/services").then((r) => setServices(r.data.services)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const wasEditing = useRef(false);
  useEffect(() => {
    if (editing && !wasEditing.current) {
      setTimeout(() => document.querySelector("[data-testid='service-editor']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
    wasEditing.current = !!editing;
  }, [editing]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, duration: parseInt(editing.duration) || 30, order: parseInt(editing.order) || 0 };
      if (editing.id) {
        await api.put(`/admin/services/${editing.id}`, payload);
      } else {
        await api.post("/admin/services", payload);
      }
      toast.success("Prestation enregistrée");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette prestation ?")) return;
    await api.delete(`/admin/services/${id}`);
    toast.success("Prestation supprimée");
    load();
  };

  const toggleAdminOnly = async (s) => {
    try {
      await api.put(`/admin/services/${s.id}`, { ...s, admin_only: !s.admin_only });
      toast.success(!s.admin_only ? "Prestation réservée à l'admin" : "Prestation visible sur le site");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const duplicate = async (s) => {
    try {
      const maxOrder = Math.max(...services.map((x) => x.order ?? 0), 0);
      await api.post("/admin/services", {
        name: `${s.name} (copie)`,
        category: s.category,
        price_label: s.price_label,
        duration: s.duration,
        description: s.description,
        active: s.active,
        admin_only: s.admin_only || false,
        order: maxOrder + 1,
      });
      toast.success("Prestation dupliquée");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const move = async (index, dir) => {
    const j = index + dir;
    if (!services || j < 0 || j >= services.length) return;
    const a = services[index];
    const b = services[j];
    let orderA = a.order ?? index;
    let orderB = b.order ?? j;
    if (orderA === orderB) { orderA = index; orderB = j; }
    try {
      await Promise.all([
        api.put(`/admin/services/${a.id}`, { ...a, order: orderB }),
        api.put(`/admin/services/${b.id}`, { ...b, order: orderA }),
      ]);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const inputCls =
    "w-full bg-white border border-taupe/30 rounded-md px-4 py-3 text-sm text-espresso placeholder:text-espresso/40 focus:outline-none focus:ring-1 focus:ring-taupe transition-shadow duration-300";

  return (
    <div data-testid="admin-services-tab">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-espresso/60 font-light">Cliquez sur une prestation pour la modifier, utilisez les flèches pour changer l'ordre d'affichage.</p>
        <button
          data-testid="service-add-btn"
          onClick={() => setEditing({ ...EMPTY })}
          className="rounded-full bg-espresso text-cream px-5 py-2.5 text-xs tracking-[0.1em] uppercase inline-flex items-center gap-2 hover:bg-taupe transition-colors duration-300"
        >
          <Plus className="w-3.5 h-3.5" /> Ajouter
        </button>
      </div>

      {editing && (
        <div className="mb-6 bg-cream border border-taupe/30 rounded-2xl p-6 space-y-4" data-testid="service-editor">
          <div className="flex justify-between items-center">
            <h3 className="font-display text-xl text-espresso">{editing.id ? "Modifier" : "Nouvelle prestation"}</h3>
            <button onClick={() => setEditing(null)} data-testid="service-editor-close" className="text-espresso/40 hover:text-espresso">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input className={inputCls} data-testid="service-name-input" placeholder="Nom (ex : Aisselles)" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <input className={inputCls} data-testid="service-category-input" placeholder="Catégorie (ex : Laser Diode)" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
            <input className={inputCls} data-testid="service-price-input" placeholder="Tarif affiché (ex : 50 €)" value={editing.price_label} onChange={(e) => setEditing({ ...editing, price_label: e.target.value })} />
            <input className={inputCls} data-testid="service-duration-input" type="number" placeholder="Durée (minutes)" value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} />
            <input className={inputCls} data-testid="service-order-input" type="number" placeholder="Ordre d'affichage" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
            <label className="flex items-center gap-3 text-sm text-espresso/70">
              <input type="checkbox" data-testid="service-active-input" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="w-4 h-4 accent-[#A39171]" />
              Visible sur le site
            </label>
            <label className="flex items-center gap-3 text-sm text-espresso/70">
              <input type="checkbox" data-testid="service-admin-only-input" checked={!!editing.admin_only} onChange={(e) => setEditing({ ...editing, admin_only: e.target.checked })} className="w-4 h-4 accent-[#A39171]" />
              Réservée à l'admin (invisible sur le site)
            </label>
          </div>
          <textarea className={`${inputCls} min-h-[70px]`} data-testid="service-description-input" placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
          <button
            data-testid="service-save-btn"
            onClick={save}
            disabled={saving || !editing.name || !editing.category}
            className="rounded-full bg-taupe text-cream px-6 py-3 text-xs tracking-[0.1em] uppercase inline-flex items-center gap-2 hover:bg-taupe-dark transition-colors duration-300 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Enregistrer
          </button>
        </div>
      )}

      {!services ? (
        <div className="flex items-center gap-3 text-sm text-espresso/55 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : (
        <div className="space-y-2">
          {services.map((s, i) => (
            <div
              key={s.id}
              data-testid={`service-row-${s.id}`}
              onClick={() => setEditing({ ...s })}
              className={`bg-white border border-taupe/20 rounded-xl px-5 py-4 flex flex-wrap items-center justify-between gap-4 cursor-pointer hover:border-taupe hover:shadow-[0_8px_24px_rgba(44,36,27,0.06)] transition-[border-color,box-shadow] duration-300 ${!s.active ? "opacity-50" : ""}`}
            >
              <div>
                <p className="text-[10px] tracking-[0.16em] uppercase text-taupe-dark">{s.category}</p>
                <p className="font-display text-lg text-espresso flex items-center gap-2">
                  {s.name}
                  {s.admin_only && (
                    <span className="rounded-full bg-espresso/8 border border-espresso/20 text-espresso/55 px-2.5 py-0.5 text-[10px] tracking-[0.08em] uppercase inline-flex items-center gap-1" data-testid={`admin-only-badge-${s.id}`}>
                      <EyeOff className="w-3 h-3" strokeWidth={1.5} /> Admin
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex flex-col -my-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    data-testid={`service-up-${s.id}`}
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    aria-label="Monter"
                    className="w-7 h-6 rounded-md border border-taupe/30 flex items-center justify-center text-espresso/55 hover:border-taupe hover:text-espresso transition-colors duration-300 disabled:opacity-25"
                  >
                    <ChevronUp className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                  <button
                    data-testid={`service-down-${s.id}`}
                    onClick={() => move(i, 1)}
                    disabled={i === services.length - 1}
                    aria-label="Descendre"
                    className="w-7 h-6 rounded-md border border-taupe/30 flex items-center justify-center text-espresso/55 hover:border-taupe hover:text-espresso transition-colors duration-300 disabled:opacity-25 mt-1"
                  >
                    <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
                <span className="text-sm text-espresso/60">{s.duration} min</span>
                <span className="font-display text-lg text-taupe-dark">{s.price_label}</span>
                <span className="w-9 h-9 rounded-full border border-taupe/30 flex items-center justify-center text-espresso/50" data-testid={`service-edit-${s.id}`}>
                  <Pencil className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <button
                  data-testid={`service-admin-only-${s.id}`}
                  onClick={(e) => { e.stopPropagation(); toggleAdminOnly(s); }}
                  aria-label="Réservée à l'admin"
                  title={s.admin_only ? "Rendre visible sur le site" : "Réserver à l'admin (masquer du site)"}
                  className={`transition-colors duration-300 ${s.admin_only ? "text-espresso" : "text-espresso/40 hover:text-espresso"}`}
                >
                  {s.admin_only ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                </button>
                <button
                  data-testid={`service-duplicate-${s.id}`}
                  onClick={(e) => { e.stopPropagation(); duplicate(s); }}
                  aria-label="Dupliquer"
                  className="text-espresso/40 hover:text-espresso transition-colors duration-300"
                >
                  <Copy className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button data-testid={`service-delete-${s.id}`} onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="text-espresso/40 hover:text-[#9E4747] transition-colors duration-300">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
